import { Box, Input, Select, Spinner, VStack } from "@chakra-ui/react";
import { useQuery } from "react-query";
import React, { useState } from "react";

/**
 * A widget that shows a list of options and allows the user to select one.
 * The options are fetched from a provider function, which is called with the current text.
 * The selected option is passed to the onSelect callback.
 *
 * The widget is either a select, or a text-entry field with a list of options.
 *
 * Options can be any type
 *
 * @param {Object} props
 * @param {Function} props.onSelect - The callback function that is called when an option is selected.
 * @param {Function} props.optionsProvider text -> Option[] or Promise<Option[]> Function to fetch filtered options
 * @param {Function?} props.optionRenderer - The function that is called to render an option. toString() by default.
 * @param {string?} props.placeholder - The placeholder text for the text-entry field.
 * @param {number?} props.maxVisibleOptions Default 10. If there are less options, the widget will show a select.
 *
 * @returns
 */
export default function AutoComplete({
                                       onSelect, optionsProvider, optionRenderer, placeholder,
                                       maxVisibleOptions = 10, noOptionsMessage = "No options available"
                                     }) {
  const [nonce] = useState(Math.random()); // for query key
  const [text, setText] = useState("");
  let queryKey = ["autoComplete", nonce, text];
  // useQuery should cache the results, and only re-fetch if the text changes
  // keep the options open while fetching a more filtered list
  const [options, setOptions] = useState([]);
  let { isLoading } = useQuery(queryKey,
    () => {
      const newOptionsOrPromise = optionsProvider(text);
      // is a Promise?
      if (newOptionsOrPromise instanceof Promise) {
        newOptionsOrPromise.then(options => {
          setOptions(options);
        });
      } else {
        setOptions(newOptionsOrPromise);
      }
      return newOptionsOrPromise;
    }
  );

  const handleSelect = (optionValue) => {
    // widget clean up
    let optionText = typeof optionValue === "string" ? optionValue : optionRenderer(optionValue);
    if (typeof optionText === "string") {
      setText(optionText);
    }
    // done = call the callback
    onSelect(optionValue);
  };

  // offer a select if there are only a few options
  let useSelect = !text && options && options.length < maxVisibleOptions;
  if (!text && isLoading && maxVisibleOptions >= 1000) {
    useSelect = true; // HACK: super high visible options will force a select
  }
  if (useSelect) {
    return (
      <>
        <Select
          placeholder={placeholder || "Select"}
          onChange={e => handleSelect(options[e.target.value])}
          onClick={(e) => e.stopPropagation()}
        >
          {options.map((optionValue, i) =>
            <option key={i} value={i}>{optionRenderer ? optionRenderer(optionValue) : "" + optionValue}</option>
          )}
          {options.length === 0 && <option value={noOptionsMessage}>{noOptionsMessage}</option>}
        </Select>
        {isLoading && <Spinner size="sm" />}
      </>
    );
  }
  // type to filter
  return <Box>
    <Input placeholder={placeholder} value={text} onChange={e => setText(e.target.value)} />
    <VStack>
      {isLoading && <Box><Spinner size="sm" /></Box>}
      {options && options.map((option, i) =>
        <AutoCompleteOption key={i} optionValue={option} optionRenderer={optionRenderer} handleSelect={handleSelect} />
      )}
      {options?.length === 0 && <Box>{noOptionsMessage}</Box>}
    </VStack>
  </Box>;
}

function AutoCompleteOption({ optionValue, optionRenderer, handleSelect }) {
  return <Box onClick={() => handleSelect(optionValue)}>
    {optionRenderer ? optionRenderer(optionValue) : "" + optionValue}
  </Box>;
};



